
import { useState, useEffect, useRef } from 'react';
import { toast } from '../../../components/toast';

interface OpeningBalanceModalProps {
  onConfirm: (amount: number, note: string, denominations?: CashDenominations) => void;
  onCancel: () => void;
  cashierName: string;
  storeName: string;
  lane: string;
  maxAmount?: number;
}

interface CashDenominations {
  bills: {
    thousand: number;
    fiveHundred: number;
    twoHundred: number;
    hundred: number;
    fifty: number;
    twenty: number;
    ten: number;
    five: number;
    two: number;
    one: number;
  };
}

const BILL_DENOMINATIONS: { key: keyof CashDenominations['bills']; value: number }[] = [
  { key: 'thousand', value: 1000 },
  { key: 'fiveHundred', value: 500 },
  { key: 'twoHundred', value: 200 },
  { key: 'hundred', value: 100 },
  { key: 'fifty', value: 50 },
  { key: 'twenty', value: 20 },
  { key: 'ten', value: 10 },
  { key: 'five', value: 5 },
  { key: 'two', value: 2 },
  { key: 'one', value: 1 },
];

const EMPTY_DENOMINATIONS: CashDenominations = {
  bills: { thousand: 0, fiveHundred: 0, twoHundred: 0, hundred: 0, fifty: 0, twenty: 0, ten: 0, five: 0, two: 0, one: 0 },
};

export default function OpeningBalanceModal({
  onConfirm,
  onCancel,
  cashierName,
  storeName,
  lane,
  maxAmount = 5000,
}: OpeningBalanceModalProps) {
  const [amount, setAmount] = useState(maxAmount > 0 ? String(maxAmount) : '');
  const [showDenominations, setShowDenominations] = useState(false);
  const [denominations, setDenominations] = useState<CashDenominations>(EMPTY_DENOMINATIONS);
  const [error, setError] = useState('');

  const calculateDenominationTotal = () =>
    BILL_DENOMINATIONS.reduce((sum, bill) => sum + denominations.bills[bill.key] * bill.value, 0);

  // Keep the opening cash amount patched to the denomination breakdown while it's in use
  useEffect(() => {
    if (showDenominations) {
      setAmount(calculateDenominationTotal().toFixed(2));
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDenominations, denominations]);

  const handleAmountChange = (value: string) => {
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setAmount(value);
      setError('');
    }
  };

  const handleDenominationChange = (key: keyof CashDenominations['bills'], value: string) => {
    const digitsOnly = value.replace(/\D/g, '');
    const numValue = digitsOnly === '' ? 0 : parseInt(digitsOnly, 10);
    setDenominations({ bills: { ...denominations.bills, [key]: numValue } });
  };

  const handleToggleDenominations = () => {
    const next = !showDenominations;
    setShowDenominations(next);
    if (!next) {
      setDenominations(EMPTY_DENOMINATIONS);
    }
  };

  const handleConfirm = () => {
    const numAmount = parseFloat(amount);
    if (!amount || isNaN(numAmount)) { setError('Please enter a valid opening balance amount'); return; }
    if (numAmount < 0) { setError('Opening balance cannot be negative'); return; }
    if (maxAmount > 0 && numAmount > maxAmount) {
      toast.error('Sorry! counter opening cash amount exceeds outlet opening cash amount!');
      return;
    }
    setError('');
    onConfirm(numAmount, '', showDenominations ? denominations : undefined);
  };

  const handleConfirmRef = useRef<() => void>(() => {});
  useEffect(() => { handleConfirmRef.current = handleConfirm; });
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') handleConfirmRef.current();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white px-6 py-4 rounded-t-lg flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl flex-shrink-0">
              <i className="ri-safe-2-line text-2xl"></i>
            </div>
            <div>
              <h2 className="text-xl font-bold leading-tight">Set Opening Balance</h2>
              <p className="text-teal-100 text-xs">Start your shift with initial cash amount</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Manager</p>
                <p className="text-sm font-semibold text-gray-900 truncate">{cashierName}</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Outlet</p>
                <p className="text-sm font-semibold text-gray-900 truncate">{storeName}</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Counter</p>
                <p className="text-sm font-semibold text-gray-900 truncate">{lane}</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Time</p>
                <p className="text-sm font-semibold text-gray-900 truncate">{new Date().toLocaleString()}</p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-start gap-2">
              <i className="ri-error-warning-line text-base text-amber-600 flex-shrink-0 mt-0.5"></i>
              <div className="text-amber-800">
                <p className="font-semibold text-sm mb-1">Important Notice</p>
                <ul className="list-disc list-inside space-y-0.5 text-xs leading-snug">
                  <li>Can only be set once per shift</li>
                  <li>Cannot be edited after confirming</li>
                  <li>Verify physical cash before entering</li>
                </ul>
              </div>
            </div>
          </div>

          <div>
            <button onClick={handleToggleDenominations} className="flex items-center gap-2 text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer">
              <i className={`ri-${showDenominations ? 'checkbox' : 'checkbox-blank'}-line text-lg`}></i>
              Use cash denomination breakdown
            </button>
          </div>

          {showDenominations && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">Cash Denomination Breakdown</h3>
                <span className="text-xs text-gray-400">Auto-calculated as you type</span>
              </div>

              <div className="grid grid-cols-5 gap-3">
                {BILL_DENOMINATIONS.map((bill) => (
                  <div key={bill.key} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-gray-700">৳{bill.value.toLocaleString()}</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="0"
                        value={denominations.bills[bill.key] || ''}
                        onChange={(e) => handleDenominationChange(bill.key, e.target.value)}
                        className="w-14 px-2 py-1 text-sm text-center border border-gray-300 rounded focus:border-teal-500 focus:outline-none"
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500 text-right">= ৳{(denominations.bills[bill.key] * bill.value).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {showDenominations ? (
            <div className="bg-teal-700 text-white rounded-lg px-4 py-3 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-teal-100">Opening Cash Amount</p>
                <p className="text-[11px] text-teal-100">Auto-calculated from denominations</p>
              </div>
              <p className="text-2xl font-bold">৳{calculateDenominationTotal().toLocaleString()}</p>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Opening Cash Amount <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-bold text-gray-400">৳</span>
                <input
                  type="text"
                  value={amount}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-10 pr-4 py-3 text-2xl font-bold border-2 border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none text-right"
                  autoFocus
                />
              </div>
            </div>
          )}
          {error && (
            <p className="text-sm text-red-600 flex items-center gap-2">
              <i className="ri-error-warning-line"></i>
              {error}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t bg-gray-50 flex gap-3 flex-shrink-0">
          <button onClick={onCancel} className="flex-1 px-4 py-3 bg-white hover:bg-gray-100 border-2 border-gray-300 text-gray-700 rounded-lg font-medium text-sm transition-colors whitespace-nowrap cursor-pointer">
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!amount || parseFloat(amount) < 0}
            className="flex-1 px-4 py-3 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-semibold text-sm transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
          >
            <i className="ri-check-line text-base"></i>
            Confirm Opening Balance
          </button>
        </div>
      </div>
    </div>
  );
}
